import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Menu, X, Landmark, FileText, Briefcase, Gavel, ArrowUpRight, Sparkles,
  Phone, Mail, MapPin, ArrowRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* Brand palette (matches paccione-cpa.com) */
const CHARCOAL = "#211C19";
const CHARCOAL_2 = "#2B2420";
const CREAM = "#F4EEE2";
const CREAM_2 = "#ECE4D4";
const INK = "#241F1B";
const ROSE = "#A96E83";      // primary accent (on light)
const ROSE_L = "#C58EA1";    // accent on dark sections

const PORTAL_URL = "https://auth.sharefile.io/paccionecpa381/login?returnUrl=%2fconnect%2fauthorize%2fcallback%3fclient_id%3dDzi4UPUAg5l8beKdioecdcnmHUTWWln6%26state%3d.6TQROvRjfAIhftT8uNRLA--%26acr_values%3dtenant%253Apaccionecpa381%26response_type%3dcode%26redirect_uri%3dhttps%253A%252F%252Fpaccionecpa381.sharefile.com%252Flogin%252Foauthlogin%26scope%3dsharefile%253Arestapi%253Av3%2520sharefile%253Arestapi%253Av3-internal%2520offline_access%2520openid";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    variants={fadeUp}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.25 }}
    transition={{ delay }}
  >
    {children}
  </motion.div>
);

const Overline = ({ children, color = ROSE }) => (
  <span className="overline flex items-center gap-3" style={{ color }}>
    <span className="h-px w-8" style={{ background: color }} />
    {children}
  </span>
);

const SparkleField = ({ sparkles }) => (
  <>
    {sparkles.map((s, i) => (
      <Sparkles
        key={i}
        className="sparkle absolute pointer-events-none"
        style={{ top: s.top, left: s.left, color: s.color, width: s.size, height: s.size, animationDelay: s.delay, animationDuration: s.dur }}
      />
    ))}
  </>
);

const HERO_SPARKLES = [
  { top: "20%", left: "6%", size: 24, color: ROSE_L, delay: "0s", dur: "3.2s" },
  { top: "16%", left: "40%", size: 14, color: "#E6C6D2", delay: "0.6s", dur: "2.6s" },
  { top: "30%", left: "62%", size: 20, color: ROSE_L, delay: "1.2s", dur: "3.6s" },
  { top: "46%", left: "12%", size: 12, color: "#E6C6D2", delay: "0.3s", dur: "2.9s" },
  { top: "52%", left: "34%", size: 18, color: ROSE_L, delay: "1.6s", dur: "3.1s" },
  { top: "40%", left: "78%", size: 15, color: "#E6C6D2", delay: "0.9s", dur: "3.4s" },
  { top: "64%", left: "70%", size: 22, color: ROSE_L, delay: "2s", dur: "3.8s" },
  { top: "70%", left: "20%", size: 13, color: "#E6C6D2", delay: "1.1s", dur: "2.7s" },
  { top: "26%", left: "88%", size: 16, color: ROSE_L, delay: "0.5s", dur: "3.3s" },
  { top: "58%", left: "52%", size: 11, color: "#E6C6D2", delay: "2.3s", dur: "3s" },
];

const HEADING_SPARKLES = [
  { top: "-6%", left: "0%", size: 18, color: ROSE, delay: "0s", dur: "3s" },
  { top: "10%", left: "30%", size: 12, color: "#C68CA1", delay: "1s", dur: "2.6s" },
  { top: "-2%", left: "58%", size: 15, color: ROSE, delay: "1.8s", dur: "3.4s" },
];

/* ------------------------------- NAV ------------------------------- */
const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "Who We Serve", href: "#clients" },
  { label: "Why Us", href: "#why" },
  { label: "Testimonials", href: "#testimonials" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="main-nav"
      className="fixed top-0 inset-x-0 z-50 transition-colors duration-500"
      style={{
        background: scrolled ? "rgba(33,28,25,0.9)" : "transparent",
        borderBottom: scrolled ? "1px solid rgba(244,238,226,0.12)" : "1px solid transparent",
        backdropFilter: scrolled ? "blur(16px)" : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <a href="#top" data-testid="nav-logo" className="flex items-center">
          <span className="font-serif-display uppercase" style={{ color: ROSE_L, fontSize: "1.6rem", fontWeight: 600, letterSpacing: "0.06em", lineHeight: 1 }}>
            Paccione CPA
          </span>
        </a>

        <div className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm transition-colors duration-300 hover:opacity-100"
              style={{ color: "rgba(244,238,226,0.75)" }}
            >
              {l.label}
            </a>
          ))}
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-portal"
            className="text-sm font-medium uppercase tracking-wider px-5 py-3 border transition-colors duration-300 hover:bg-white/10"
            style={{ color: CREAM, borderColor: "rgba(244,238,226,0.3)" }}
          >
            Client Portal
          </a>
          <a
            href="#contact"
            data-testid="nav-cta"
            className="text-sm font-medium uppercase tracking-wider px-6 py-3 transition-opacity duration-300 hover:opacity-90"
            style={{ background: ROSE, color: CREAM }}
          >
            Contact Us
          </a>
        </div>

        <button
          className="md:hidden"
          style={{ color: CREAM }}
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden px-6 py-6 flex flex-col gap-5" style={{ background: CHARCOAL, borderTop: "1px solid rgba(244,238,226,0.12)" }}>
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-lg"
              style={{ color: "rgba(244,238,226,0.85)" }}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="text-center py-3 uppercase tracking-wider text-sm font-medium border"
            style={{ color: CREAM, borderColor: "rgba(244,238,226,0.3)" }}
            data-testid="mobile-nav-portal"
          >
            Client Portal
          </a>
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            className="text-center py-3 uppercase tracking-wider text-sm font-medium"
            style={{ background: ROSE, color: CREAM }}
            data-testid="mobile-nav-cta"
          >
            Contact Us
          </a>
        </div>
      )}
    </nav>
  );
};

/* ------------------------------- HERO ------------------------------- */
const Hero = () => (
  <header id="top" className="relative min-h-screen flex items-end overflow-hidden" style={{ background: CHARCOAL }}>
    <div className="absolute inset-0">
      <img
        src="https://images.unsplash.com/photo-1603673329776-28248af53fc0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHx3YXJtJTIwZWxlZ2FudCUyMG1vZGVybiUyMG9mZmljZSUyMHByb2Zlc3Npb25hbCUyMGFkdmlzb3J5JTIwbWVldGluZ3xlbnwwfHx8fDE3ODM4MTEwMjR8MA&ixlib=rb-4.1.0&q=85"
        alt="Warm modern advisory office"
        className="w-full h-full object-cover opacity-75"
      />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #211C19 4%, rgba(33,28,25,0.4) 45%, rgba(33,28,25,0.04))" }} />
    </div>

    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-40 w-full">
      <SparkleField sparkles={HERO_SPARKLES} />
      <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.15 } } }}>
        <motion.h1
          variants={fadeUp}
          className="font-serif-display font-light leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-8xl max-w-4xl"
          style={{ color: CREAM }}
        >
          Big-firm expertise.
          <br />
          <span className="italic" style={{ color: ROSE_L }}>Small-firm care.</span>
        </motion.h1>
        <motion.p
          variants={fadeUp}
          className="mt-8 text-lg max-w-xl leading-relaxed font-light"
          style={{ color: "rgba(244,238,226,0.75)" }}
        >
          Guiding small business owners with the clarity,
          strategy, and judgment needed to navigate every financial moment.
        </motion.p>
        <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row gap-4">
          <a
            href="#contact"
            data-testid="hero-cta-primary"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 font-medium uppercase tracking-wider text-sm transition-opacity duration-300 hover:opacity-90"
            style={{ background: ROSE, color: CREAM }}
          >
            Schedule a Consultation <ArrowRight size={16} />
          </a>
          <a
            href="#services"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center justify-center gap-2 border px-8 py-4 font-medium uppercase tracking-wider text-sm transition-colors duration-300 hover:bg-white/10"
            style={{ borderColor: "rgba(244,238,226,0.3)", color: CREAM }}
          >
            Explore Services
          </a>
        </motion.div>
      </motion.div>
    </div>
  </header>
);

/* ------------------------------ WHY US ------------------------------ */
const WhyUs = () => (
  <section id="why" className="py-28 md:py-40" style={{ background: CREAM }}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-2 gap-16 items-end">
        <Reveal>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight" style={{ color: INK }}>
            Because your decisions <span className="italic" style={{ color: ROSE }}>deserve certainty.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-lg leading-relaxed font-light" style={{ color: "#6B6157" }}>
            We help you build and maintain a reliable financial setup that feels calm and easy to manage.
            You get straightforward guidance, consistent support, and a team that keeps things clear so
            decisions don't feel overwhelming.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ----------------------------- SERVICES ----------------------------- */
const SERVICES = [
  { icon: Landmark, title: "Accounting & Bookkeeping", tag: "The Foundation",
    desc: "From monthly bookkeeping and compliance to financial reporting and monthly financial review meetings, we support your financial success at every stage. Acting as an extension of your team, we manage the day-to-day financial operations so you can stay focused on your mission with confidence.", span: "lg:col-span-6" },
  { icon: FileText, title: "Tax Planning & Preparation", tag: "Certainty",
    desc: "Accurate, timely federal and state filings paired with proactive, year-round tax strategy — eliminating surprises, reducing liability, and keeping you and your business safely ahead of changing law.", span: "lg:col-span-6" },
  { icon: Briefcase, title: "Advisory Services", tag: "Strategic",
    desc: "We also support focused accounting and finance needs including bookkeeping cleanup, entity structuring, strategic planning, bank financing support, and tax advisory as needed.", span: "lg:col-span-6" },
  { icon: Gavel, title: "IRS Representation", tag: "Protection",
    desc: "Direct handling of audits, notices, penalties, and resolution — with steady, informed guidance at your side throughout the entire process.", span: "lg:col-span-6" },
];
const ServiceCard = ({ s, index, onBook }) => {
  const Icon = s.icon;
  const [flipped, setFlipped] = useState(false);
  return (
    <div className="h-72 md:h-80" style={{ perspective: "1400px" }}>
      <div
        data-testid={`service-card-${index}`}
        className="relative w-full h-full cursor-pointer"
        style={{
          transformStyle: "preserve-3d",
          transition: "transform 0.6s cubic-bezier(0.22,1,0.36,1)",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
        onMouseEnter={() => setFlipped(true)}
        onMouseLeave={() => setFlipped(false)}
        onClick={() => setFlipped((f) => !f)}
      >
        {/* FRONT */}
        <div
          className="absolute inset-0 p-8 md:p-10 flex flex-col justify-between overflow-hidden"
          style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", background: "#FFFFFF", border: "1px solid rgba(36,31,27,0.08)", borderTop: "3px solid #A96E83", boxShadow: "0 16px 44px -26px rgba(36,31,27,0.35)" }}
        >
          <div className="absolute -top-12 -left-10 pointer-events-none" style={{ width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(169,110,131,0.28), transparent 70%)", filter: "blur(22px)" }} />
          <div className="absolute top-16 right-4 pointer-events-none" style={{ width: 120, height: 120, borderRadius: "50%", background: "radial-gradient(circle, rgba(198,140,161,0.22), transparent 70%)", filter: "blur(18px)" }} />
          <Icon size={168} strokeWidth={0.5} className="absolute -bottom-8 -right-8 pointer-events-none" style={{ color: ROSE, opacity: 0.06 }} />
          <div className="relative flex items-start justify-between">
            <div className="inline-flex items-center justify-center rounded-full" style={{ width: 58, height: 58, background: "rgba(169,110,131,0.12)" }}>
              <Icon size={26} strokeWidth={1.5} style={{ color: ROSE }} />
            </div>
            <span className="font-serif-display text-4xl leading-none" style={{ color: "rgba(169,110,131,0.35)" }}>{String(index + 1).padStart(2, "0")}</span>
          </div>
          <div className="relative">
            <h3 className="font-serif-display text-2xl md:text-3xl leading-tight" style={{ color: INK }}>{s.title}</h3>
            <p className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]" style={{ color: ROSE }}>Hover to view <ArrowUpRight size={14} /></p>
          </div>
        </div>
        {/* BACK */}
        <div
          className="absolute inset-0 p-8 md:p-10 flex flex-col justify-between overflow-hidden"
          style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "rotateY(180deg)", background: "linear-gradient(150deg, #B67E92 0%, #8E5A70 100%)", boxShadow: "0 16px 44px -26px rgba(36,31,27,0.5)" }}
        >
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 80% 0%, rgba(255,255,255,0.18), transparent 55%)" }} />
          <Icon size={168} strokeWidth={0.5} className="absolute -bottom-8 -right-8 pointer-events-none" style={{ color: "#FFFFFF", opacity: 0.08 }} />
          <div className="relative">
            <p className="overline" style={{ color: CREAM, opacity: 0.9 }}>{s.title}</p>
            <p className="mt-3 text-sm md:text-base leading-relaxed font-light" style={{ color: CREAM }}>{s.desc}</p>
          </div>
          <button
            data-testid={`service-book-${index}`}
            onClick={(e) => { e.stopPropagation(); onBook(s.title); }}
            className="relative self-start inline-flex items-center gap-2 px-6 py-3 text-sm font-medium uppercase tracking-wider transition-opacity duration-300 hover:opacity-90"
            style={{ background: CREAM, color: "#8E5A70" }}
          >
            Book Now <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

const BookingModal = ({ open, onClose, defaultService }) => {
  const [form, setForm] = useState({ name: "", email: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open) setForm({ name: "", email: "", service: defaultService || "", message: "" });
  }, [open, defaultService]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Thank you. We'll be in touch shortly.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent py-3 focus:outline-none transition-colors duration-300";
  const inputStyle = { borderBottom: "1px solid rgba(36,31,27,0.2)", color: INK };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div className="absolute inset-0" style={{ background: "rgba(15,12,10,0.72)", backdropFilter: "blur(6px)" }} onClick={onClose} />
          <motion.div
            data-testid="booking-modal"
            className="relative w-full max-w-lg p-8 md:p-10"
            style={{ background: CREAM }}
            initial={{ scale: 0.95, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 24, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              onClick={onClose}
              data-testid="booking-modal-close"
              className="absolute top-4 right-4 hover:opacity-60 transition-opacity"
              style={{ color: INK }}
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <p className="overline" style={{ color: ROSE }}>Consultation Request</p>
            <h3 className="mt-3 font-serif-display text-3xl md:text-4xl leading-tight" style={{ color: INK }}>
              Let's talk.
            </h3>
            <form onSubmit={submit} data-testid="booking-form" className="mt-8 space-y-6">
              <input data-testid="booking-input-name" className={inputCls} style={inputStyle} placeholder="Full name *" value={form.name} onChange={update("name")} />
              <input data-testid="booking-input-email" type="email" className={inputCls} style={inputStyle} placeholder="Email address *" value={form.email} onChange={update("email")} />
              <select data-testid="booking-input-service" aria-label="Service of interest" className={`${inputCls} appearance-none`} style={inputStyle} value={form.service} onChange={update("service")}>
                <option value="">Select a service…</option>
                {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <textarea data-testid="booking-input-message" rows={3} className={inputCls} style={inputStyle} placeholder="How can we help? *" value={form.message} onChange={update("message")} />
              <button
                type="submit"
                disabled={loading}
                data-testid="booking-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 font-medium uppercase tracking-wider text-sm transition-opacity duration-300 hover:opacity-90 disabled:opacity-60"
                style={{ background: ROSE, color: CREAM }}
              >
                {loading ? "Sending…" : "Request Consultation"} <ArrowRight size={16} />
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Services = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState("");
  const openBooking = (title) => { setSelectedService(title); setModalOpen(true); };

  return (
    <section id="services" className="py-28 md:py-40" style={{ background: "linear-gradient(160deg, #F7F1E8 0%, #F1E4E4 100%)" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl relative">
          <SparkleField sparkles={HEADING_SPARKLES} />
          <Reveal><Overline>What We Do</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight" style={{ color: INK }}>
              Reliable service that keeps your <span className="italic" style={{ color: ROSE }}>business moving forward.</span>
            </h2>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {SERVICES.map((s, i) => (
            <Reveal key={i} delay={(i % 3) * 0.1} className={s.span}>
              <ServiceCard s={s} index={i} onBook={openBooking} />
            </Reveal>
          ))}
        </div>
      </div>

      <BookingModal open={modalOpen} onClose={() => setModalOpen(false)} defaultService={selectedService} />
    </section>
  );
};

/* --------------------------- WHO WE SERVE --------------------------- */
const CLIENTS = [
  { title: "Small & Mid-Sized Business Owners",
    img: "https://images.unsplash.com/photo-1485217988980-11786ced9454?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjB3b3JraW5nfGVufDB8fHx8MTc4MzgwOTE5N3ww&ixlib=rb-4.1.0&q=85",
    desc: "Whether launching, scaling, or stabilizing, we deliver clear advisory, accounting, and tax services tailored to your stage of growth." },
  { title: "Founders & Private Clients",
    img: "https://images.unsplash.com/photo-1505843513577-22bb7d21e455?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxoaWdoJTIwbmV0JTIwd29ydGglMjBsdXh1cnklMjBsaWZlc3R5bGUlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzgzODA5MjE0fDA&ixlib=rb-4.1.0&q=85",
    desc: "Steady, discreet support for individuals and families with complex financial lives — tax strategy, financial affairs organization, and long-term planning." },
];
const WhoWeServe = () => (
  <section id="clients" className="py-28 md:py-40" style={{ background: CREAM }}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal className="relative"><SparkleField sparkles={HEADING_SPARKLES} /><Overline>Who We Serve</Overline></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-6 font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-3xl" style={{ color: INK }}>
          For business owners and <span className="italic" style={{ color: ROSE }}>founders alike.</span>
        </h2>
      </Reveal>

      <div className="mt-16 grid md:grid-cols-2 gap-8">
        {CLIENTS.map((c, i) => (
          <Reveal key={i} delay={i * 0.15}>
            <div data-testid={`client-card-${i}`} className="group relative overflow-hidden">
              <div className="h-[440px] overflow-hidden">
                <img
                  src={c.img}
                  alt={c.title}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(33,28,25,0.92), rgba(33,28,25,0.3) 55%, transparent)" }} />
              </div>
              <div className="absolute bottom-0 p-8 md:p-10">
                <h3 className="font-serif-display text-3xl leading-tight" style={{ color: CREAM }}>{c.title}</h3>
                <p className="mt-4 leading-relaxed font-light max-w-md" style={{ color: "rgba(244,238,226,0.8)" }}>{c.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* --------------------------- TESTIMONIALS --------------------------- */
const Testimonials = () => (
  <section id="testimonials" className="py-28 md:py-40" style={{ background: "#F1E8E7" }}>
    <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
      <Reveal><div className="flex justify-center"><Overline>Letters From Clients</Overline></div></Reveal>
      <Reveal delay={0.15}>
        <blockquote className="mt-10 font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.2] font-light" style={{ color: INK }}>
          “This team goes above and beyond and helps you through the hard knocks of business.
          They are the best thing you can do for your business.”
        </blockquote>
      </Reveal>
      <Reveal delay={0.25}>
        <div className="mt-10 flex flex-col items-center">
          <span className="h-px w-12 mb-5" style={{ background: ROSE }} />
          <p style={{ color: INK }}>Hugh Clark</p>
          <p className="text-sm" style={{ color: "rgba(36,31,27,0.5)" }}>JaniPlus</p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ------------------------------ CONTACT ------------------------------ */
const SERVICE_OPTIONS = [
  "Accounting & Bookkeeping", "Tax Planning & Preparation",
  "Advisory Services", "IRS Representation", "General Inquiry",
];
const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Thank you. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent py-3 focus:outline-none transition-colors duration-300";
  const inputStyle = { borderBottom: "1px solid rgba(36,31,27,0.2)", color: INK };

  return (
    <section id="contact" className="py-28 md:py-40" style={{ background: CREAM_2 }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
        <Reveal className="relative">
          <SparkleField sparkles={HEADING_SPARKLES} />
          <Overline>Let's Get Started</Overline>
          <p className="mt-6 text-lg leading-relaxed font-light max-w-md" style={{ color: "#6B6157" }}>
            Schedule a complimentary consultation. We'll listen, review your situation,
            and show you where there is room to improve.
          </p>

          <div className="mt-12 space-y-6">
            <a href="tel:2102551717" data-testid="contact-phone" className="flex items-center gap-4 hover:opacity-70 transition-opacity" style={{ color: INK }}>
              <Phone size={18} style={{ color: ROSE }} /> (210) 255-1717
            </a>
            <a href="mailto:info@paccione-cpa.com" data-testid="contact-email" className="flex items-center gap-4 hover:opacity-70 transition-opacity" style={{ color: INK }}>
              <Mail size={18} style={{ color: ROSE }} /> info@paccione-cpa.com
            </a>
            <div className="flex items-center gap-4" style={{ color: INK }}>
              <MapPin size={18} style={{ color: ROSE }} /> 8131 Hausman Rd W, San Antonio, TX 78249
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
            <div className="grid sm:grid-cols-2 gap-8">
              <input data-testid="input-name" className={inputCls} style={inputStyle} placeholder="Full name *" value={form.name} onChange={update("name")} />
              <input data-testid="input-email" type="email" className={inputCls} style={inputStyle} placeholder="Email address *" value={form.email} onChange={update("email")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
              <input data-testid="input-phone" className={inputCls} style={inputStyle} placeholder="Phone" value={form.phone} onChange={update("phone")} />
              <select data-testid="input-service" aria-label="Service of interest" className={`${inputCls} appearance-none`} style={inputStyle} value={form.service} onChange={update("service")}>
                <option value="">Interested in…</option>
                {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <textarea data-testid="input-message" rows={4} className={inputCls} style={inputStyle} placeholder="How can we help? *" value={form.message} onChange={update("message")} />
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 px-10 py-4 font-medium uppercase tracking-wider text-sm transition-opacity duration-300 hover:opacity-90 disabled:opacity-60"
              style={{ background: ROSE, color: CREAM }}
            >
              {loading ? "Sending…" : "Request Consultation"} <ArrowRight size={16} />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

/* ------------------------------ FOOTER ------------------------------ */
const Footer = () => (
  <footer className="pt-24 pb-12" style={{ background: CHARCOAL, borderTop: "1px solid rgba(244,238,226,0.1)" }}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid md:grid-cols-3 gap-12">
        <div>
          <span className="font-serif-display text-3xl" style={{ color: CREAM }}>Paccione CPA</span>
          <p className="mt-4 leading-relaxed font-light max-w-xs" style={{ color: "rgba(244,238,226,0.65)" }}>
            The future-ready CPA firm for small business owners and high-net-worth families.
          </p>
        </div>
        <div>
          <p className="overline mb-5" style={{ color: ROSE_L }}>Explore</p>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a href={l.href} className="transition-opacity duration-300 hover:opacity-100" style={{ color: "rgba(244,238,226,0.65)" }}>{l.label}</a>
              </li>
            ))}
            <li>
              <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-portal" className="transition-opacity duration-300 hover:opacity-100" style={{ color: ROSE_L }}>Client Portal · Sign In</a>
            </li>
          </ul>
        </div>
        <div>
          <p className="overline mb-5" style={{ color: ROSE_L }}>Contact</p>
          <ul className="space-y-3" style={{ color: "rgba(244,238,226,0.65)" }}>
            <li>(210) 255-1717</li>
            <li>info@paccione-cpa.com</li>
            <li>8131 Hausman Rd W<br />San Antonio, TX 78249</li>
          </ul>
        </div>
      </div>
      <div className="mt-20 pt-8 flex flex-col md:flex-row justify-between gap-4 text-sm" style={{ borderTop: "1px solid rgba(244,238,226,0.1)", color: "rgba(244,238,226,0.4)" }}>
        <p>© {new Date().getFullYear()} Paccione CPA. All rights reserved.</p>
        <p>AICPA Standards · Texas-Licensed CPA · Enrolled Agent</p>
      </div>
    </div>
  </footer>
);

/* ------------------------------ PAGE ------------------------------ */
export default function Landing() {
  return (
    <div style={{ background: CHARCOAL }}>
      <Navbar />
      <Hero />
      <WhyUs />
      <Services />
      <WhoWeServe />
      <Testimonials />
      <Contact />
      <Footer />
    </div>
  );
}
